import { useState, useCallback, useMemo, useEffect, useRef } from "react";

/**
 * @param {{
 *   options: { value: string; label: string; disabled?: boolean }[];
 *   value: string[];
 *   searchable: boolean;
 *   maxSelected?: number;
 *   isOpen: boolean;
 *   setIsOpen: (v: boolean) => void;
 *   dropdownRef?: React.RefObject<HTMLElement | null>;
 * }} params
 */
export function useMultiSelect({
  options,
  value,
  searchable,
  maxSelected,
  isOpen,
  setIsOpen,
  dropdownRef,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q) ||
        String(o.group || "").toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      if (searchable) {
        const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
        return () => window.clearTimeout(t);
      }
    } else {
      setSearchQuery("");
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return;
    const max = Math.max(0, filteredOptions.length - 1);
    setHighlightedIndex((i) => Math.min(i, max));
  }, [filteredOptions.length, isOpen]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      const inContainer = containerRef.current.contains(e.target);
      const inDropdown = dropdownRef?.current?.contains(e.target);
      if (!inContainer && !inDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [setIsOpen, dropdownRef]);

  const atMax =
    maxSelected != null && value.length >= maxSelected;

  const toggleValue = useCallback(
    (val) => {
      const opt = options.find((o) => o.value === val);
      if (opt?.disabled) return value;
      if (value.includes(val)) {
        return value.filter((v) => v !== val);
      }
      if (atMax) return value;
      return [...value, val];
    },
    [value, options, atMax]
  );

  const selectHighlighted = useCallback(() => {
    const row = filteredOptions[highlightedIndex];
    if (!row || row.disabled) return null;
    if (atMax && !value.includes(row.value)) return null;
    return toggleValue(row.value);
  }, [filteredOptions, highlightedIndex, toggleValue, atMax, value]);

  return {
    searchQuery,
    setSearchQuery,
    highlightedIndex,
    setHighlightedIndex,
    filteredOptions,
    containerRef,
    searchInputRef,
    atMax,
    toggleValue,
    selectHighlighted,
  };
}
